import { useEffect, createContext, useMemo, useContext, forwardRef } from 'react';
import { jsx, Fragment } from 'react/jsx-runtime';
import { createSyncStoragePersister } from '@tanstack/query-sync-storage-persister';
import { useQuery, QueryClient, QueryClientContext, QueryClientProvider } from '@tanstack/react-query';
import { persistQueryClient } from '@tanstack/react-query-persist-client';
import { getAPIPath } from '@interhyp/platform-request';
import clsx from 'clsx';
import { Helmet } from 'react-helmet';

/**
 * Converts a hex color into a RGB color.
 * @param hex
 */
function hex2Rgb(hex) {
  let c = [...hex.slice(1)];
  if (c.length === 3) {
    c = [c[0], c[0], c[1], c[1], c[2], c[2]];
  }
  const hexColor = Number.parseInt(`0x${c.join('')}`, 16);
  // eslint-disable-next-line no-bitwise
  return [hexColor >> 16 & 255, hexColor >> 8 & 255, hexColor & 255];
}

/**
 * Converts an HSL color value to RGB. Conversion formula
 * adapted from http://en.wikipedia.org/wiki/HSL_color_space.
 * Assumes h, s, and l are contained in the set [0, 1] and
 * returns r, g, and b in the set [0, 255].
 *
 * @param   {number}  h       The hue
 * @param   {number}  s       The saturation
 * @param   {number}  l       The lightness
 * @return  {Array}           The RGB representation
 */

function hslToRgb(h, s, l) {
  const _h = h / 360;
  const _s = s / 100;
  const _l = l / 100;
  let r;
  let g;
  let b;
  if (_s === 0) {
    r = _l;
    g = _l;
    b = _l;
  } else {
    const hue2rgb = (p, q, t) => {
      let _t = t;
      if (_t < 0) {
        _t += 1;
      }
      if (_t > 1) {
        _t -= 1;
      }
      if (_t < 1 / 6) {
        return p + (q - p) * 6 * _t;
      }
      if (_t < 1 / 2) {
        return q;
      }
      if (_t < 2 / 3) {
        return p + (q - p) * (2 / 3 - _t) * 6;
      }
      return p;
    };
    const q = _l < 0.5 ? _l * (1 + _s) : _l + _s - _l * _s;
    const p = 2 * _l - q;
    r = hue2rgb(p, q, _h + 1 / 3);
    g = hue2rgb(p, q, _h);
    b = hue2rgb(p, q, _h - 1 / 3);
  }
  return [Math.round(r * 255), Math.round(g * 255), Math.round(b * 255)];
}

function rgb2Hex(r, g, b) {
  // eslint-disable-next-line no-bitwise
  return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
}

/**
 * Converts an RGB color value to HSL. Conversion formula
 * adapted from http://en.wikipedia.org/wiki/HSL_color_space.
 * Assumes r, g, and b are contained in the set [0, 255] and
 * returns h, s, and l in the set [0, 1].
 *
 * @param   {number}  r       The red color value
 * @param   {number}  g       The green color value
 * @param   {number}  b       The blue color value
 * @return  {Array}           The HSL representation
 */
function rgbToHsl(r, g, b) {
  const _r = r / 255;
  const _g = g / 255;
  const _b = b / 255;
  const max = Math.max(_r, _g, _b);
  const min = Math.min(_r, _g, _b);
  let h;
  let s;
  const l = (max + min) / 2;
  if (max === min) {
    // achromatic
    h = 0;
    s = 0;
  } else {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case _r:
        h = (_g - _b) / d + (_g < _b ? 6 : 0);
        break;
      case _g:
        h = (_b - _r) / d + 2;
        break;
      case _b:
        h = (_r - _g) / d + 4;
        break;
    }

    // @ts-expect-error Will get removed as soon as platform theme is refactored
    h /= 6;
  }
  // @ts-expect-error Will get removed as soon as platform theme is refactored
  return [360 * h, 100 * s, 100 * l];
}

function darken(color, amount) {
  const rgb = hex2Rgb(color);
  const hslColor = rgbToHsl(rgb[0], rgb[1], rgb[2]);
  const hslColorL = hslColor[2] > 100 ? 100 : hslColor[2];
  const rgbDarkened = hslToRgb(hslColor[0], hslColor[1], hslColorL - amount >= 0 ? hslColorL - amount : 0);
  return rgb2Hex(rgbDarkened[0], rgbDarkened[1], rgbDarkened[2]);
}

function isBrightColor(color) {
  const rgb = hex2Rgb(color);
  const yiq = (rgb[0] * 299 + rgb[1] * 587 + rgb[2] * 114) / 1000;
  return yiq > 170;
}

/**
 * Returns true if given color is a valid hex color.
 * @param color
 */
function isColor(color) {
  return /^#[\da-f]{6}$/iu.test(color);
}

function lighten(color, amount) {
  const rgb = hex2Rgb(color);
  const hslColor = rgbToHsl(rgb[0], rgb[1], rgb[2]);
  const hslColorL = hslColor[2] < 0 ? 0 : hslColor[2];
  const rgbLightened = hslToRgb(hslColor[0], hslColor[1], hslColorL + amount <= 100 ? hslColorL + amount : 100);
  return rgb2Hex(rgbLightened[0], rgbLightened[1], rgbLightened[2]);
}

const ThemeTypes = ['primary', 'secondary', 'tertiary'];

/**
 * Calculates a theme from a single color.
 * @param color
 * @param type
 */
function calculateTheme(color, type) {
  if (!isColor(color)) {
    throw new Error('Invalid Hex Color');
  }
  return {
    [`--color-${type}`]: color,
    [`--color-${type}-ripple`]: lighten(color, 35),
    [`--color-${type}-hover`]: darken(color, 15),
    [`--color-${type}-hover-ripple`]: color,
    [`--color-${type}-text`]: isBrightColor(color) ? '#333333' : '#ffffff',
    [`--color-${type}-light`]: lighten(color, 42)
  };
}

// normaly imported but only part of home not platform:
const setCSSVariable = (name, val) => {
  if (window?.document?.body) {
    window.document.body.style.setProperty(name, val);
  }
};
const setCSSVariables = config => {
  const excludedStyleVariables = new Set(['media-base-path', 'logo-path', 'visual-path', 'copyright', 'font-type']);
  Object.keys(config.style).forEach(propertyName => {
    if (!excludedStyleVariables.has(propertyName)) {
      setCSSVariable(`--${propertyName}`, config.style[propertyName]);
    }
  });
  setCalculatedThemeVariables(config);
};
const setCalculatedThemeVariables = config => {
  const colors = [{
    hex: config.style['color-primary'],
    name: 'primary'
  }, {
    hex: config.style['color-secondary'],
    name: 'secondary'
  }, {
    hex: config.style['color-tertiary'],
    name: 'tertiary'
  }];
  colors.forEach(color => {
    if (color.hex) {
      const theme = calculateTheme(color.hex, color.name);
      Object.keys(theme).forEach(key => {
        setCSSVariable(key, theme[key]);
      });
    }
  });
};
function useTheme({
  style,
  tenantCode
}) {
  useEffect(() => {
    if (style) {
      setCSSVariables({
        style,
        tenantCode
      });
    }
  }, [style, tenantCode]);
}

const ThemeContext = /*#__PURE__*/createContext({});
const defaultTheme = {
  primaryColor: '#579EB0',
  secondaryColor: '#60B187',
  tertiaryColor: '#8BC44A',
  backgroundColor: '#FFFFFF',
  fontType: 'Roboto'
};
function ThemeProvider({
  children,
  theme
}) {
  useEffect(() => {
    document.body.style.setProperty('--font-family', theme.fontType);
    document.body.style.setProperty('--visual-url', theme.visualPath ? `url(${theme.visualPath})` : '');
    document.body.style.setProperty('--color-background', theme.backgroundColor);
    ThemeTypes.forEach(type => {
      const themeColor = calculateTheme(theme[`${type}Color`], type);
      for (const key in themeColor) {
        document.body.style.setProperty(key, themeColor[key]);
      }
    });
  }, [theme]);
  return /*#__PURE__*/jsx(ThemeContext.Provider, {
    value: theme,
    children: children
  });
}

const clientThemeContext = /*#__PURE__*/createContext(undefined);

function useFqdnQuery(fqdn) {
  return useQuery({
    queryKey: [fqdn],
    queryFn: async () => {
      const query = new URLSearchParams({
        fqdn
      });
      const baseUrl = getAPIPath('company');
      const requestPath = `${baseUrl}/bff-v1/full-company-information?${query.toString()}`;
      const response = await fetch(requestPath);
      if (!response.ok) {
        throw new Error(`Could not load theme for fqdn ${fqdn}`);
      }
      return response.json();
    }
  });
}

function ClientThemeProvider({
  children,
  fqdn,
  fallback = null
}) {
  const {
    isLoading,
    isSuccess,
    isError,
    data
  } = useFqdnQuery(fqdn);
  const theme = useMemo(() => {
    if (isSuccess && data) {
      return {
        fontType: 'Roboto',
        companyId: data.companyId,
        companyName: data.companyName,
        configurableLinks: data.configurableLinks,
        logoUri: data.style.logoPath ? `/s3${data.style.logoPath}` : undefined,
        visualPath: data.style.visualPath ? `/s3${data.style.visualPath}` : undefined,
        primaryColor: data.style.colorPrimary ?? defaultTheme.primaryColor,
        secondaryColor: data.style.colorSecondary ?? defaultTheme.secondaryColor,
        tertiaryColor: data.style.colorTertiary ?? defaultTheme.tertiaryColor,
        backgroundColor: data.style.colorBackground ?? defaultTheme.backgroundColor,
        processColor: data.style.colorProcess
      };
    }
  }, [isSuccess, data]);
  if (isLoading) {
    return null;
  }
  if (isError) {
    return /*#__PURE__*/jsx(Fragment, {
      children: fallback
    });
  }
  if (!theme) {
    return null;
  }
  return /*#__PURE__*/jsx(clientThemeContext.Provider, {
    value: theme,
    children: /*#__PURE__*/jsx(ThemeProvider, {
      theme: theme,
      children: children
    })
  });
}

const CACHE_TIME = 1000 * 60 * 60 * 24; // 24 Hours

function ClientThemeProviderConnected({
  children,
  fqdn
}) {
  const queryClient = useMemo(() => new QueryClient({
    defaultOptions: {
      queries: {
        refetchOnWindowFocus: false,
        refetchIntervalInBackground: false,
        refetchInterval: false,
        retry: false,
        gcTime: CACHE_TIME,
        // 24 hours
        staleTime: CACHE_TIME
      }
    }
  }), []);
  useEffect(() => {
    persistQueryClient({
      queryClient,
      maxAge: CACHE_TIME,
      persister: createSyncStoragePersister({
        storage: window.localStorage
      })
    });
  }, [queryClient]);

  // do not use useQueryClient because it throws an error if no client was setup
  const outerQueryClient = useContext(QueryClientContext);
  return /*#__PURE__*/jsx(QueryClientProvider, {
    client: queryClient,
    children: /*#__PURE__*/jsx(ClientThemeProvider, {
      fqdn: fqdn,
      children: outerQueryClient ? /*#__PURE__*/jsx(QueryClientProvider, {
        client: outerQueryClient,
        children: children
      }) : children
    })
  });
}

function useClientTheme() {
  const context = useContext(clientThemeContext);
  if (!context) {
    throw new Error('`clientThemeContext()` must be called within a Provider');
  }
  return context;
}

var styles = {"logo":"logo_--Poffk2fn"};

const Logo = /*#__PURE__*/forwardRef(({
  className,
  ...props
}, ref) => {
  const theme = useClientTheme();
  if (!theme?.logoUri) {
    return null;
  }
  return /*#__PURE__*/jsx("img", {
    alt: "logo",
    height: 35,
    ...props,
    className: clsx(className, styles.logo),
    ref: ref,
    src: theme.logoUri
  });
});

function createFavicon(primaryColor) {
  const canvas = document.createElement('canvas');
  canvas.width = 400;
  canvas.height = 400;
  const context = canvas.getContext('2d');
  context.strokeStyle = primaryColor;
  context.lineWidth = 22;
  context.beginPath();
  context.moveTo(48, 325);
  context.lineTo(48, 170);
  context.lineTo(152, 75);
  context.lineTo(256, 170);
  context.lineTo(256, 325);
  context.stroke();
  context.lineWidth = 33;
  context.beginPath();
  context.moveTo(165, 322);
  context.lineTo(165, 171);
  context.lineTo(264, 75);
  context.lineTo(364, 171);
  context.lineTo(364, 322);
  context.stroke();
  return canvas.toDataURL();
}

function Favicon() {
  const theme = useClientTheme();
  const favicon = useMemo(() => {
    if (theme) {
      return createFavicon(theme.primaryColor);
    }
  }, [theme]);
  if (!favicon) {
    return null;
  }
  return /*#__PURE__*/jsx(Helmet, {
    children: /*#__PURE__*/jsx("link", {
      rel: "shortcut icon",
      href: favicon
    })
  });
}

export { ClientThemeProviderConnected as ClientThemeProvider, Favicon, Logo, clientThemeContext as MockClientThemeContext, ThemeContext, ThemeProvider, ThemeTypes, calculateTheme, darken, defaultTheme, hex2Rgb, hslToRgb, isBrightColor, isColor, lighten, rgb2Hex, rgbToHsl, setCSSVariable, useClientTheme, useTheme };
//# sourceMappingURL=index.js.map
